import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { AlertType, ErrorType, PageType, ReplaceParam } from '@lib/interfaces';
import { CommonLibService, PageLoadingService } from '@lib/services';
import { PostAddCompanionRequest } from 'src/sdk_checkin/model/models';
import { Passenger, PassengerSearchType } from '../../interface/checkin-passenger-addition';
import { CheckinBaseClass } from '../../components';
import { CheckinCommonStoreService } from '../checkin-common-store/checkin-common-store.service';
import { CheckinPassengerAdditionResultStoreService } from './checkin-passenger-addition-result-store.service';
import { ErrorCodeConstants, WarningCodeConstants } from '@conf/app.constants';

@Injectable()
export class CheckinPassengerAdditionService extends CheckinBaseClass {
  /** 同行者追加APIレスポンス正常通知イベント */
  isPassengerAdditionSuccess$ = new Subject<boolean | void>();
  /** 同行者追加APIレスポンス異常メッセージコード */
  private static readonly ApiErrorMessage: { [errorCode: string]: string } = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000109]: 'E0305',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000355]: 'E0790',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000111]: 'E0307',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000112]: 'E0308',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000113]: 'E0310',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000114]: 'E0311',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000115]: 'E0312',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000116]: 'E0313',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000007]: 'E0314',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000117]: 'E0316',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000119]: 'E0318',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000120]: 'E0319',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000121]: 'E0320',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000122]: 'E0322',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000123]: 'E0323',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000124]: 'E0324',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000723]: 'E1268',
  };
  /** 同行者追加APIレスポンスワーニングメッセージコード */
  private static readonly ApiWarningMessage: { [warningCode: string]: string } = {
    [WarningCodeConstants.WARNING_CODES.WBAZ000047]: 'W0309',
  };

  /**
   * 搭乗者数
   */
  get numberOfTravelers(): number {
    return this._checkinCommonStoreService.checkinCommon.numberOfTravelers!;
  }

  constructor(
    private _commonLibService: CommonLibService,
    private _checkinCommonStoreService: CheckinCommonStoreService,
    private _checkinPassengerAdditionResultStoreService: CheckinPassengerAdditionResultStoreService,
    private _pageLoadingService: PageLoadingService
  ) {
    super();
    this.watchPassengerAdditionSuccess();
    this.watchPassengerAdditionFailure();
  }

  override destroy(): void {}

  /**
   * 同行者追加APIリクエスト
   * @param passenger 同行者情報
   */
  addPassenger(passenger: Passenger): void {
    const searchType = this.convertRequestSearchType(passenger.searchType);
    if (searchType) {
      this._checkinPassengerAdditionResultStoreService.request({
        journeyId: this._checkinCommonStoreService.checkinCommon.journeyId!,
        searchType: searchType,
        companions: passenger.passengers,
      });
    } else {
      // ローディング画面表示終了
      this._pageLoadingService.endLoading();
    }
  }

  /**
   * 検索条件を同行者追加APIリクエストパラメータの検索方法コードへ変換する
   * @param searchType 検索条件
   * @returns 同行者追加APIリクエストの検索方法コード
   */
  private convertRequestSearchType(
    searchType: PassengerSearchType
  ): PostAddCompanionRequest.SearchTypeEnum | undefined {
    if (searchType === PassengerSearchType.ORDER) {
      return PostAddCompanionRequest.SearchTypeEnum.Order;
    }

    if (searchType === PassengerSearchType.E_TICKET) {
      return PostAddCompanionRequest.SearchTypeEnum.ETicket;
    }

    return;
  }

  /**
   * 同行者追加APIレスポンス正常取得
   */
  private watchPassengerAdditionSuccess(): void {
    this.subscribeService(this._checkinPassengerAdditionResultStoreService.success$, (state) => {
      if (state.warnings) {
        const matchWarningMsgId = CheckinPassengerAdditionService.ApiWarningMessage[state.warnings[0].code];
        if (matchWarningMsgId) {
          this._commonLibService.alertMessageStoreService.setAlertWarningMessage({
            contentHtml: matchWarningMsgId,
            isCloseEnable: true,
            alertType: AlertType.WARNING,
          });
        }
      }

      // ローディング画面表示終了
      this._pageLoadingService.endLoading();
      this.isPassengerAdditionSuccess$.next(true);
    });
  }

  /**
   * 同行者追加APIレスポンス異常取得
   */
  private watchPassengerAdditionFailure(): void {
    this.subscribeService(
      this._checkinPassengerAdditionResultStoreService.failure$.pipe(
        withLatestFrom(this._commonLibService.apiErrorResponseService.getApiErrorResponse$(), (_, state) => state)
      ),
      (state) => {
        const apiError = state?.errors?.[0];
        if (apiError) {
          const matchErrorMsgId = CheckinPassengerAdditionService.ApiErrorMessage[apiError.code];
          if (matchErrorMsgId) {
            const apiErrorSource = apiError.source as { items: string[] };
            let errorMsgParams: ReplaceParam[] = [];
            if (apiErrorSource?.items && !isNaN(Number(apiErrorSource?.items[0]))) {
              let index = Number(apiErrorSource.items[0]);
              errorMsgParams = [{ key: 0, value: ++index, dontTranslate: true }];
            }
            this._commonLibService.errorsHandlerService.setRetryableError(PageType.SUBPAGE, {
              errorMsgId: matchErrorMsgId,
              apiErrorCode: apiError.code,
              params: errorMsgParams,
            });
          } else {
            this._commonLibService.errorsHandlerService.setNotRetryableError({
              errorType: ErrorType.SYSTEM,
              apiErrorCode: apiError.code,
            });
          }
        }
        // ローディング画面表示終了
        this._pageLoadingService.endLoading();
      }
    );
  }
}
